import { useNavigate, useLocation } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import "./CadastroUsuario.css";
import CadastroHeader from "../../../components/CadastroHeader/CadastroHeader";
import { api } from "../../../services/apiService";
import { useAuth } from "../../../contexts/AuthContext";
import { rotas } from "../../../app/configs/CadastroConfigs";
import Modal from "../../../components/Modal/Modal";
import AppSnackbar from "../../../components/AppSnackbar/AppSnackbar";
import { Box, Button, Paper } from "@mui/material";
import styles from "../../../styles/styles";

function CadastroUsuario() {
  const navigate = useNavigate();
  const location = useLocation();
  const usuarioEdit = location.state?.id ? location.state : null;
  const { userInfo } = useAuth();
  const getInitialForm = (usuario) => ({
    id: usuario?.id || null,
    email: usuario?.email || "",
    roleId: usuario?.roleId || "",
    roleNome: usuario?.roleNome || "—",
    ativo: usuario?.ativo || true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const [form, setForm] = useState(getInitialForm(usuarioEdit));
  const [emailError, setEmailError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const [modal, setModal] = useState({
    show: false,
    type: "",
    message: "",
  });

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setLoadingRoles(true);
        const data = await api.roles.getAll();
        const payload = data?.data || data || {};
        setRoles(payload.items || payload);
      } catch (error) {
        setModal({ show: true, type: "error", message: "Erro ao carregar roles. Tente novamente." });
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  const validateForm = useCallback(() => {
    if (!form.email || !form.roleId)
      return "Preencha todos os campos antes de salvar!";
    if (emailError) return "Verifique o Email antes de salvar!";
    return null;
  }, [form, emailError]);

    const handleChange = useCallback((e) => {
      const { name, value } = e.target;

      if (name === "email" && userInfo.id === form.id) {
        return;
      }

      let sanitizedValue = value;
      if (name === "email") {
        sanitizedValue = value.trim().toLowerCase().replace(/\s/g, "");
      }

      setForm((prev) => ({ ...prev, [name]: sanitizedValue }));

      if (name === "email") {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setEmailError(sanitizedValue && !regex.test(sanitizedValue));
      }

    }, [userInfo.id, form.id]);

  const handleSave = useCallback(async () => {
    const validationMessage = validateForm();
    if (validationMessage) {
      setModal({ show: true, type: "error", message: validationMessage });
      return;
    }

    try {
      setLoading(true);
      const isEdit = !!form.id;

      const response = await api.usuario.getAll(0, 0);
      const payload = response?.data || response || {};
      const usuariosExistentes = payload.items || payload;

      const usuarioDuplicado = usuariosExistentes.find(
        u => u.email?.toLowerCase() === form.email?.toLowerCase()
          && u.id !== form.id // Permite salvar o mesmo usuário em edição
      );

      if (usuarioDuplicado) {
        setModal({ show: true, type: "error", message: `Já existe um usuário com o email "${form.email}". Por favor, escolha outro email.` });
        setLoading(false);
        return;
      }

      isEdit
        ? await api.usuario.update(form.id, form)
        : await api.usuario.create(form);
      setSnackbar({ open: true, message: `Usuário ${form.id ? "atualizado" : "criado"} com sucesso!` , severity: 'success' });

      setTimeout(() => {
        setModal({ ...modal, show: false });
        navigate(rotas["cadastroPage"], { state: { activeTab: "usuario" } });
      }, 2000);

    } catch (error) {
      let message = error.response?.data?.message
        || 'Erro ao salvar usuário.';

      if (error.response?.status === 403) {
        message = "Você não tem permissão para essa ação.";
      } else {
        message = error.response.data?.message ||
          error.response.data?.error ||
          error.mensagem ||
          message;
      }

      setModal({
        show: true,
        type: 'error',
        message: message,
        onConfirm: null
      });
    } finally {
      setLoading(false);
    }
  }, [form, validateForm, navigate, usuarioEdit, roles]);

  return (
    <>
      <Box sx={styles.pageWrapper}>


        <Box component="main" sx={styles.mainContent}>

          <Paper elevation={0} sx={styles.card}>

            <CadastroHeader
              title={usuarioEdit ? "Editar Usuário" : "Cadastrar Usuário"}
              subtitle={
                usuarioEdit
                  ? "Edite os dados do usuário"
                  : "Adicione novos usuários e vincule-os a um perfil"
              }
              backRoute={rotas["cadastroPage"]}
              backTab="usuario"
            />

            <Modal
              show={modal.show}
              type={modal.type}
              message={modal.message}
              onClose={() => setModal({ ...modal, show: false })}
            />

            <div className="form-card">

              <label>E-mail</label>

              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className={emailError ? "input-error" : ""}
                placeholder="Digite o e-mail"
                disabled={userInfo.id === form.id}
                style={
                  userInfo.id === form.id
                    ? { opacity: 0.3, cursor: "not-allowed" }
                    : {}
                }
              />

              {emailError && <p className="error-text">E-mail inválido</p>}

              <label>Perfil</label>

              <select
                name="roleId"
                value={form.roleId ? String(form.roleId) : ""}
                onChange={handleChange}
                disabled={loadingRoles}
              >
                <option value="">Selecione um perfil</option>

                {roles.map((role) => (
                  <option key={role.id} value={String(role.id)}>
                    {role.nome}
                  </option>
                ))}

              </select>
              <p>
                Administrador — Acesso a todos os módulos do sistema.
              </p>
              <p>
                Supervisor — Visualização de logs, alterar locação de trens, remover operador (não gerencia cadastro).

              </p>
              <p>
                Operador — Ciência de alarmes, com foco em monitoramento dos trens na linha selecionada ao acessar o sistema.

              </p>
              <p>
                Visualizador — Somente visualização de logs da telemetria.

              </p>
              <p>
                Auditor — Auditoria de logs dos trens e usuários.

              </p>

              {loadingRoles && <p>Carregando perfis...</p>}

              <Button
                type="button"
                onClick={handleSave}
                disabled={loading || emailError}
              >
                {loading ? "Salvando..." : "Salvar"}
              </Button>

            </div>

          </Paper>

        </Box>

      </Box>

      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </>

  );

}

export default CadastroUsuario;
